import { useState, useEffect, useCallback } from 'react';
import { DeliveryConfigApi } from '@shared/sdk-v1/apis';
import { DeliveryConfig } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { useAuth } from '../auth/hooks/useAuth';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { getCourierId, getCourierSetting } from '@state/courierSlice';

export const useDeliveryAvailability = () => {
  const [deliveryConfig, setDeliveryConfig] = useState<DeliveryConfig | null>(
    null
  );
  // Initialize as true since we need to fetch config before we can determine availability
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const courierId = useSelector((state: RootState) => getCourierId(state));
  const courierSettings = useSelector((state: RootState) => getCourierSetting(state));

  // Fetch courier's delivery configuration
  const fetchDeliveryConfig = useCallback(async () => {
    if (!courierId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const configApi = new DeliveryConfigApi(apiConfig());

      const filter = {
        where: {
          courierId: courierId,
        },
      };

      const response = await configApi.deliveryConfigFind({
        filter: JSON.stringify(filter),
      });

      if (response.length > 0) {
        setDeliveryConfig(response[0]);
      }
    } catch (err: any) {
      console.error('Error fetching delivery config:', err);
      setError('Failed to load delivery configuration');
    } finally {
      setIsLoading(false);
    }
  }, [courierId]);

  // Check if delivery services are available
  const isDeliveryAvailable = useCallback(() => {
    // First check if delivery is enabled in courier settings
    if (!courierSettings?.isDeliveryEnabled) return false;

    if (!deliveryConfig) return false;

    const hasActiveOptions =
      deliveryConfig.inHouseActive ||
      deliveryConfig.knutsfordActive ||
      deliveryConfig.zipMailActive;

    return hasActiveOptions;
  }, [deliveryConfig, courierSettings?.isDeliveryEnabled]);

  // Load delivery configuration when component mounts
  useEffect(() => {
    fetchDeliveryConfig();
  }, [fetchDeliveryConfig]);

  return {
    deliveryConfig,
    isLoading,
    error,
    isDeliveryAvailable: isDeliveryAvailable(),
    refetch: fetchDeliveryConfig,
  };
};
